// ODS-EWO-033 P5. The Attention Center in the chrome rather than only behind a
// sidebar link: one count of what is waiting for this person, in every
// application at once. It holds no queue of its own — the platform answers,
// filtered by the caller's permission and the organization's entitlements, and
// what it could not include is stated rather than folded into the number.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AttentionWorkItems } from "@odingard/sdk-client";
import { shellAttentionApi } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "answered"; items: AttentionWorkItems }
  | { kind: "unavailable" };

function isItems(data: unknown): data is AttentionWorkItems {
  return Boolean(data) && Array.isArray((data as AttentionWorkItems).workItems);
}

function withheldNotice(items: AttentionWorkItems): string | null {
  const total =
    items.withheld.not_entitled +
    items.withheld.permission_denied +
    items.withheld.permission_unresolvable;
  return total > 0
    ? `${total} more are not yours to see, and are not counted.`
    : null;
}

export function AttentionIndicator({ orgId }: { orgId: string }) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!orgId) {
      setState({ kind: "unavailable" });
      return;
    }
    let live = true;
    setState({ kind: "loading" });
    shellAttentionApi
      .workItems(orgId, { state: "open" })
      .then((response) => {
        if (!live) return;
        if (response.mode === "unavailable" || !isItems(response.data)) {
          setState({ kind: "unavailable" });
          return;
        }
        setState({ kind: "answered", items: response.data });
      })
      .catch(() => {
        if (live) setState({ kind: "unavailable" });
      });
    return () => {
      live = false;
    };
  }, [orgId]);

  // Degraded, and said so. A shell that shows nothing when the platform did not
  // answer claims there is nothing waiting (ODS-UX-001 §11).
  if (state.kind === "unavailable") {
    return (
      <Link
        className="attention-indicator attention-indicator--unavailable"
        to="/studio/attention"
        aria-label="Attention Center. We could not ask what needs attention."
        title="We could not ask what needs attention."
      >
        <span aria-hidden="true">⚡</span>
        <span className="attention-indicator__count">—</span>
      </Link>
    );
  }

  if (state.kind === "loading") {
    return (
      <span
        className="attention-indicator attention-indicator--loading"
        aria-label="Loading what needs attention"
      >
        <span aria-hidden="true">⚡</span>
      </span>
    );
  }

  const count = state.items.workItems.length;
  const withheld = withheldNotice(state.items);
  const label =
    count === 0
      ? "Attention Center. Nothing is waiting for you."
      : `Attention Center. ${count} ${count === 1 ? "item needs" : "items need"} your attention.`;

  return (
    <Link
      className={`attention-indicator ${count > 0 ? "attention-indicator--waiting" : ""}`}
      to="/studio/attention"
      aria-label={withheld ? `${label} ${withheld}` : label}
      title={withheld ?? label}
    >
      <span aria-hidden="true">⚡</span>
      <span className="attention-indicator__count">{count}</span>
    </Link>
  );
}
